import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { GetReadingRoomReadingQuery } from '../../../generated/graphql'
import { RootState } from '../../../store'
import { LiveParticipants } from '../hooks/useLiveParticipants'
import { AgoraTokenPayload } from '../lib/agoraTokenHandler'
import getAgoraToken from '../services/getAgoraToken'
import getReadingRoomReading from '../services/getReadingRoomReading'
import sendReadingRoomChatMessage from '../services/sendReadingRoomChatMessage'
import { VirtualBackground } from '../../../components/agoraRTC/useVideoCall'
import { Participants } from '../../readingLobby/hooks/useLiveParticipants'
import { HLO } from '../../../types/global'

export const readingStateRank: Record<HLO.ReadingState, number> = {
  Scheduled: 1,
  Starting: 2,
  'In Progress': 3,
  Ending: 4,
  Canceled: 5,
  Incomplete: 6,
  Completed: 7,
}

const actionPrefix = `readingRoom`

export type ReadingRoomClientType = 'presenter' | 'audience' | 'self'

export type ReadingRoomMessage = {
  author: string
  authorUserId: string
  clientType: ReadingRoomClientType
  message: string
  id: string
}

export type ReadingRoomState = {
  agoraToken?: AgoraTokenPayload
  agoraTokenError: boolean
  agoraTokenLoading: boolean
  bookPresent: boolean
  lobbyOpen: boolean
  virtualBackground: VirtualBackground
  chatOpen: boolean
  unreadMessageCount: number
  clientType?: ReadingRoomClientType
  confirmCancelOpen: boolean
  handsRaised: {
    [key: string]: {
      lastUpdated: string
    }
  }
  hasAccess?: boolean
  insertMessageLoading: boolean
  messages: ReadingRoomMessage[]
  muted: boolean
  participants: Participants
  liveParticipants: LiveParticipants
  reading?: GetReadingRoomReadingQuery['reading']
  readingError: boolean
  readingLoading: boolean
  readingState: HLO.ReadingState | undefined
  raiseHand: boolean
  timeElapsed?: number
}

const initialState: ReadingRoomState = {
  agoraTokenError: false,
  agoraTokenLoading: true,
  virtualBackground: 'none',
  chatOpen: false,
  unreadMessageCount: 0,
  clientType: 'audience',
  bookPresent: false,
  lobbyOpen: true,
  confirmCancelOpen: false,
  readingState: undefined,
  handsRaised: {},
  insertMessageLoading: false,
  messages: [],
  muted: false,
  liveParticipants: {},
  participants: {},
  raiseHand: false,
  readingError: false,
  readingLoading: true,
  timeElapsed: undefined,
}

export const readingRoomSlice = createSlice({
  name: actionPrefix,
  initialState,
  reducers: {
    setReadingRoomReadingState: (
      state,
      { payload }: PayloadAction<HLO.ReadingState>,
    ) => {
      state.readingState = payload
    },
    setReadingRoomLobbyOpen: (state, { payload }: PayloadAction<boolean>) => {
      state.lobbyOpen = payload
    },
    setReadingRoomBookPresent: (state, { payload }: PayloadAction<boolean>) => {
      state.bookPresent = payload
    },

    setReadingRoomLiveParticipants: (
      state,
      { payload }: PayloadAction<ReadingRoomState['liveParticipants']>,
    ) => {
      state.liveParticipants = payload
    },

    setReadingRoomChatMessages: (
      state,
      { payload }: PayloadAction<ReadingRoomState['messages']>,
    ) => {
      state.messages = payload
    },
    setReadingRoomChatOpen: (state, { payload }: PayloadAction<boolean>) => {
      state.chatOpen = payload
      if (payload) state.unreadMessageCount = 0
    },
    setReadingRoomUnreadMessageCount: (
      state,
      { payload }: PayloadAction<number>,
    ) => {
      state.unreadMessageCount = payload
    },
    setReadingRoomClientType: (
      state,
      { payload }: PayloadAction<ReadingRoomClientType>,
    ) => {
      state.clientType = payload
    },
    setReadingRoomConfirmCancelOpen: (
      state,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.confirmCancelOpen = payload
    },
    setReadingRoomHandRaised: (
      state,
      {
        payload: { lastUpdated, userId },
      }: PayloadAction<{ userId: string; lastUpdated: string }>,
    ) => {
      state.handsRaised[userId] = { lastUpdated }
    },
    setReadingRoomHasAccess: (state, { payload }: PayloadAction<boolean>) => {
      state.hasAccess = payload
    },
    setReadingRoomMuted: (state, { payload }: PayloadAction<boolean>) => {
      state.muted = payload
    },
    setReadingRoomRaiseHand: (state, { payload }: PayloadAction<boolean>) => {
      state.raiseHand = payload
    },
    setReadingRoomParticipants: (
      state,
      { payload }: PayloadAction<ReadingRoomState['participants']>,
    ) => {
      state.participants = payload
    },
    setReadingRoomTimeElapsed: (
      state,
      { payload }: PayloadAction<ReadingRoomState['timeElapsed']>,
    ) => {
      state.timeElapsed = payload
    },

    setReadingRoomVirtualBackground: (
      state,
      { payload }: PayloadAction<VirtualBackground>,
    ) => {
      state.virtualBackground = payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getReadingRoomReading.fulfilled, (state, { payload }) => {
        state.reading = payload.data.reading ? payload.data.reading : undefined
        state.readingError = false
        state.readingLoading = false
      })
      .addCase(getReadingRoomReading.pending, (state) => {
        state.reading = undefined
        state.readingError = false
        state.readingLoading = true
      })
      .addCase(getReadingRoomReading.rejected, (state) => {
        state.reading = undefined
        state.readingError = true
        state.readingLoading = false
      })
      .addCase(sendReadingRoomChatMessage.fulfilled, (state) => {
        if (!state.chatOpen) {
          state.chatOpen = true
        }

        state.insertMessageLoading = false
      })
      .addCase(sendReadingRoomChatMessage.pending, (state) => {
        state.insertMessageLoading = true
      })
      .addCase(sendReadingRoomChatMessage.rejected, (state) => {
        state.insertMessageLoading = false
      })
      .addCase(getAgoraToken.fulfilled, (state, { payload }) => {
        state.agoraToken = payload
        state.agoraTokenError = false
        state.agoraTokenLoading = false
      })
      .addCase(getAgoraToken.pending, (state) => {
        state.agoraTokenError = false
        state.agoraTokenLoading = true
      })
      .addCase(getAgoraToken.rejected, (state) => {
        state.agoraTokenError = true
        state.agoraTokenLoading = false
      })
  },
})

export const {
  setReadingRoomVirtualBackground,
  setReadingRoomChatMessages,
  setReadingRoomChatOpen,
  setReadingRoomUnreadMessageCount,
  setReadingRoomClientType,
  setReadingRoomConfirmCancelOpen,
  setReadingRoomHandRaised,
  setReadingRoomHasAccess,
  setReadingRoomMuted,
  setReadingRoomRaiseHand,
  setReadingRoomParticipants,
  setReadingRoomTimeElapsed,
  setReadingRoomBookPresent,
  setReadingRoomLobbyOpen,
  setReadingRoomLiveParticipants,
  setReadingRoomReadingState,
} = readingRoomSlice.actions

export const selectReadingRoomAgoraToken = ({ readingRoom }: RootState) => {
  return readingRoom.agoraToken
}

export const selectReadingRoomAgoraTokenError = ({
  readingRoom,
}: RootState) => {
  return readingRoom.agoraTokenError
}

export const selectReadingRoomAgoraTokenLoading = ({
  readingRoom,
}: RootState) => {
  return readingRoom.agoraTokenLoading
}

export const selectReadingRoomCancelled = (state: RootState) => {
  const readingState = selectReadingRoomEffectiveReadingState(state)
  return readingState === 'Canceled'
}

export const selectReadingRoomChatOpen = ({ readingRoom }: RootState) => {
  return readingRoom.chatOpen
}

export const selectReadingRoomUnreadMessageCount = ({
  readingRoom,
}: RootState) => {
  return readingRoom.unreadMessageCount
}

export const selectReadingRoomClientType = ({ readingRoom }: RootState) => {
  return readingRoom.clientType
}

export const selectReadingRoomCompleted = (state: RootState) => {
  const readingState = selectReadingRoomEffectiveReadingState(state)
  return readingState === 'Completed' || readingState === 'Incomplete'
}

export const selectReadingRoomConfirmCancelOpen = ({
  readingRoom,
}: RootState) => {
  return readingRoom.confirmCancelOpen
}

export const selectReadingRoomRaiseHand = ({ readingRoom }: RootState) => {
  return readingRoom.raiseHand
}

export const selectReadingRoomHandsRaised = ({ readingRoom }: RootState) => {
  return readingRoom.handsRaised
}

export const selectReadingRoomVirtualBackground = ({
  readingRoom,
}: RootState) => {
  return readingRoom.virtualBackground
}

export const selectReadingRoomHasAccess = ({ readingRoom }: RootState) => {
  return readingRoom.hasAccess
}

export const selectReadingRoomInsertMessageLoading = ({
  readingRoom,
}: RootState) => {
  return readingRoom.insertMessageLoading
}

export const selectReadingRoomMessages = ({ readingRoom }: RootState) => {
  return readingRoom.messages
}

export const selectReadingRoomMuted = ({ readingRoom }: RootState) => {
  return readingRoom.muted
}

export const selectReadingRoomParticipants = ({ readingRoom }: RootState) => {
  return readingRoom.participants
}

export const selectReadingRoomReading = ({ readingRoom }: RootState) => {
  return readingRoom.reading
}

export const selectReadingRoomReadingError = ({ readingRoom }: RootState) => {
  return readingRoom.readingError
}

export const selectReadingRoomReadingLoading = ({ readingRoom }: RootState) => {
  return readingRoom.readingLoading
}

export const selectReadingRoomReadingState = ({ readingRoom }: RootState) => {
  return readingRoom.readingState
}

export const selectReadingRoomEffectiveReadingState = ({
  readingRoom,
}: RootState): HLO.ReadingState | undefined => {
  const liveState = readingRoom.readingState
  const dbState = readingRoom.reading?.readingEventStatusName as
    | HLO.ReadingState
    | undefined

  if (!liveState) return dbState
  if (!dbState) return liveState

  return (readingStateRank[liveState] ?? 0) >= (readingStateRank[dbState] ?? 0)
    ? liveState
    : dbState
}

export const selectReadingRoomIsActiveState = (state: RootState): boolean => {
  const readingState = selectReadingRoomEffectiveReadingState(state)

  return readingState === 'In Progress' || readingState === 'Ending'
}

export const selectReadingRoomEnding = (state: RootState) => {
  const readingState = selectReadingRoomEffectiveReadingState(state)
  return readingState === 'Ending'
}
export const selectReadingRoomLobbyOpen = ({ readingRoom }: RootState) => {
  return readingRoom.lobbyOpen
}

export const selectReadingRoomBookPresent = ({ readingRoom }: RootState) => {
  return readingRoom.bookPresent
}

export const selectReadingRoomLiveParticipants = ({
  readingRoom,
}: RootState) => {
  return readingRoom.liveParticipants
}

export const selectReadingRoomTimeElapsed = ({ readingRoom }: RootState) => {
  return readingRoom.timeElapsed
}
